/**
 * RemoveFromOnboardingButton — escape hatch to pull a candidate OUT of
 * the onboarding funnel.
 *
 * Rendered on the candidate profile only when the candidate is currently
 * `onboarding` OR `hired` (has been through the funnel). Confirms via a
 * small dialog so we don't nuke someone's paperwork by accident. Records
 * the reason in `status_history` for the audit trail — the Journal de
 * Bord shows it as a dedicated event.
 */
import * as Dialog from "@radix-ui/react-dialog";
import { motion } from "framer-motion";
import { UserMinus, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { api } from "../../lib/api";
import { hasAnyRole } from "../../lib/roles";
import { useAuth } from "../../lib/auth";

const ALLOWED_ROLES = ["admin"];
// iter110m-hotfix3 · Coordinateur RH avec `can_remove_from_pipeline`
// peut aussi retirer un candidat de son pipeline (dans sa région).
function _canUseRemoveButton(user) {
    if (!user) return false;
    if (hasAnyRole(user, ...ALLOWED_ROLES)) return true;
    const roles = new Set([user.role, ...(user.additional_roles || [])]);
    return roles.has("coordinateur") && !!user.can_remove_from_pipeline;
}
// Only show the button for candidates that are currently in the funnel.
const SHOW_STATUSES = new Set(["onboarding", "hired"]);

const TARGET_OPTIONS = [
    { value: "validated", label: "Remettre en validé (candidat garde le pipeline)" },
    // Iter72 — the CEO wants a Vivier slot IN THIS modal so admins can
    // send a candidate to the talent pool at the very moment they're
    // pulled from the hiring circuit, without hunting for another
    // button. Uses the existing /candidates/{id}/talent-pool endpoint
    // and reuses the "cant_reach" reason as a sensible default.
    { value: "talent_pool", label: "Envoyer au vivier (recontact plus tard)", tone: "vivier" },
    { value: "set_aside", label: "Écarter (visible dans « Écartés »)" },
    { value: "rejected", label: "Refuser définitivement" },
    // Soft-delete branch — sends the candidate to /admin/corbeille where
    // it can be restored within 30 days. The hard-delete path is now
    // exposed only inside the trash page (safer default).
    { value: "delete", label: "Envoyer à la corbeille (récupérable 30 jours)", tone: "danger" },
];

// The soft-delete flow doesn't need a type-to-confirm gate anymore — the
// candidate can be restored from the trash page for 30 days. Kept as a
// no-op helper so future permanent-delete UIs can plug it back in.
const _isDeleteTargetOk = () => true;

export default function RemoveFromOnboardingButton({
    candidate,
    onDone,
    dataTestid,
    variant = "chip",   // "chip" (default profile) | "icon" (compact kanban)
    skipStatusCheck = false,
}) {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [target, setTarget] = useState("validated");
    const [comment, setComment] = useState("");
    const [deleteConfirmName, setDeleteConfirmName] = useState("");
    const [submitting, setSubmitting] = useState(false);

    if (!_canUseRemoveButton(user)) return null;
    if (!candidate) return null;
    // Show only when actually applicable — either status is onboarding/hired,
    // or there's a pending onboarding form. Callers that KNOW the card is
    // in the funnel (e.g. Embauche kanban) can bypass this check.
    if (
        !skipStatusCheck
        && !SHOW_STATUSES.has(candidate.status)
        && !candidate.onboarding_form_id
    ) {
        return null;
    }

    const submit = async (e) => {
        e?.preventDefault?.();
        setSubmitting(true);
        try {
            if (target === "delete") {
                // Soft-delete branch — moves the candidate to
                // /admin/corbeille. Fully reversible within 30 days,
                // so no type-to-confirm gate needed anymore.
                await api.delete(`/candidates/${candidate.id}`);
                toast.success("Candidat envoyé à la corbeille. Récupérable 30 jours dans /admin/corbeille.");
                setOpen(false);
                setComment("");
                setDeleteConfirmName("");
                setTarget("validated");
                onDone?.({ deleted: true, id: candidate.id });
                return;
            }
            if (target === "talent_pool") {
                // Vivier branch — remove from the hiring circuit AND flag
                // in_talent_pool=true so the candidate lands in /vivier.
                // Two API calls: first pull them out of onboarding
                // (validated) so the magic link is invalidated + journal
                // stamped, then hit /talent-pool with the "cant_reach"
                // default reason (admin can refine from /vivier).
                await api.post(`/candidates/${candidate.id}/remove-from-onboarding`, {
                    target: "validated",
                    comment: comment.trim() || null,
                });
                const { data } = await api.post(`/candidates/${candidate.id}/talent-pool`, {
                    reason: "cant_reach",
                    note: comment.trim() || "Retiré du circuit d'embauche",
                });
                toast.success(`Candidat retiré du circuit et envoyé au vivier ✅`, {
                    description: "Retrouvez-le dans /vivier pour le recontacter",
                    duration: 5000,
                });
                window.dispatchEvent(new CustomEvent("vivier:changed"));
                setOpen(false);
                setComment("");
                setTarget("validated");
                onDone?.(data);
                return;
            }
            const { data } = await api.post(
                `/candidates/${candidate.id}/remove-from-onboarding`,
                {
                    target,
                    comment: comment.trim() || null,
                },
            );
            toast.success(
                target === "validated"
                    ? "Candidat retiré du circuit — remis en validé."
                    : target === "set_aside"
                        ? "Candidat retiré du circuit et écarté."
                        : "Candidat retiré du circuit et refusé.",
            );
            setOpen(false);
            setComment("");
            setTarget("validated");
            onDone?.(data);
        } catch (err) {
            const detail = err.response?.data?.detail;
            const msg = typeof detail === "string"
                ? detail
                : detail?.message || "Impossible de retirer le candidat";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog.Root open={open} onOpenChange={setOpen}>
            <Dialog.Trigger asChild>
                {variant === "icon" ? (
                    <button
                        type="button"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={dataTestid || "remove-from-onboarding-btn"}
                        title="Retirer ce candidat du circuit d'embauche"
                        aria-label="Retirer du circuit"
                        className="inline-flex items-center justify-center h-6 w-6 rounded-full border border-red-200 bg-white/90 text-red-600 transition hover:bg-red-50 hover:border-red-300 hover:text-red-700 shadow-sm opacity-0 group-hover:opacity-100 focus:opacity-100"
                    >
                        <UserMinus className="h-3 w-3" />
                    </button>
                ) : (
                    <button
                        type="button"
                        data-testid={dataTestid || "remove-from-onboarding-btn"}
                        title="Retirer ce candidat du circuit d'embauche"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-red-200 bg-white text-red-700 text-[11px] font-black uppercase tracking-widest transition hover:bg-red-50 hover:border-red-300"
                    >
                        <UserMinus className="h-3.5 w-3.5" />
                        Retirer du circuit
                    </button>
                )}
            </Dialog.Trigger>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
                <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[95vw] max-w-md bg-white rounded-3xl shadow-2xl p-6 focus:outline-none">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2 }}
                    >
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <Dialog.Title className="text-lg font-black text-shariti-black">
                                    Retirer du circuit d&apos;embauche
                                </Dialog.Title>
                                <Dialog.Description className="text-xs text-slate-500 mt-1">
                                    Le lien magique de la fiche sera invalidé. Cette action
                                    est tracée dans le journal de bord.
                                </Dialog.Description>
                            </div>
                            <Dialog.Close asChild>
                                <button
                                    type="button"
                                    className="text-slate-400 hover:text-slate-700"
                                    aria-label="Fermer"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </Dialog.Close>
                        </div>

                        <form onSubmit={submit} className="space-y-3">
                            <div>
                                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5 block">
                                    Que faire du candidat ?
                                </label>
                                <div className="space-y-1.5">
                                    {TARGET_OPTIONS.map((opt) => {
                                        const isDanger = opt.tone === "danger";
                                        const isVivier = opt.tone === "vivier";
                                        const selected = target === opt.value;
                                        return (
                                            <label
                                                key={opt.value}
                                                className={`flex items-center gap-2 p-2 rounded-lg border-2 cursor-pointer transition ${
                                                    selected
                                                        ? isDanger
                                                            ? "border-red-500 bg-red-50"
                                                            : isVivier
                                                                ? "border-purple-500 bg-purple-50"
                                                                : "border-shariti-orange bg-shariti-orange-soft"
                                                        : isDanger
                                                            ? "border-red-100 bg-white hover:border-red-300"
                                                            : isVivier
                                                                ? "border-purple-100 bg-white hover:border-purple-300"
                                                                : "border-slate-200 bg-white hover:border-slate-300"
                                                }`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="remove-target"
                                                    value={opt.value}
                                                    checked={selected}
                                                    onChange={(e) => setTarget(e.target.value)}
                                                    className={isDanger ? "accent-red-600" : isVivier ? "accent-purple-600" : "accent-shariti-orange"}
                                                    data-testid={`remove-target-${opt.value}`}
                                                />
                                                <span className={`text-sm font-semibold ${
                                                    isDanger ? "text-red-800" : isVivier ? "text-purple-800" : "text-shariti-black"
                                                }`}>
                                                    {opt.label}
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>

                            {target === "delete" ? (
                                <div className="rounded-lg border-2 border-red-200 bg-red-50/60 p-3 space-y-2">
                                    <div className="text-[11px] text-red-900 leading-relaxed">
                                        Le candidat va être <strong>envoyé à la corbeille</strong>. Tu pourras le restaurer pendant <strong>30 jours</strong> depuis <span className="font-mono">/admin/corbeille</span>. Après ce délai, il sera <strong>définitivement supprimé</strong> avec toutes ses données (fiche, invitations Academy). Les contrats signés sont conservés (obligation légale).
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">
                                            Raison (optionnel — annotation dans la corbeille)
                                        </label>
                                        <textarea
                                            value={comment}
                                            onChange={(e) => setComment(e.target.value)}
                                            placeholder="Ex. Candidat test, double compte…"
                                            rows={2}
                                            data-testid="remove-comment"
                                            className="w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm focus:border-shariti-orange focus:outline-none"
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <label htmlFor="remove-comment" className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5 block">
                                        Raison (optionnel)
                                    </label>
                                    <textarea
                                        id="remove-comment"
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                        placeholder="Ex. Erreur d'invitation, changement d'avis…"
                                        rows={3}
                                        data-testid="remove-comment"
                                        className="w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm focus:border-shariti-orange focus:outline-none"
                                    />
                                </div>
                            )}

                            <div className="flex justify-end gap-2 pt-2">
                                <Dialog.Close asChild>
                                    <button
                                        type="button"
                                        disabled={submitting}
                                        className="px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest text-slate-600 hover:bg-slate-100"
                                    >
                                        Annuler
                                    </button>
                                </Dialog.Close>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="remove-from-onboarding-confirm"
                                    className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest text-white disabled:opacity-60 ${
                                        target === "delete"
                                            ? "bg-red-700 hover:bg-red-800"
                                            : "bg-red-600 hover:bg-red-700"
                                    }`}
                                >
                                    {submitting
                                        ? "En cours…"
                                        : target === "delete"
                                            ? "Envoyer à la corbeille"
                                            : "Confirmer"}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
